import React, { useState } from "react";
import { FileText } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { useUpdateConsumerDocument } from "../../hooks";

interface DocumentItemProps {
  name: string;
  doc: any[];
  uploadDate: string;
  consumerStatus: string;
  documentId: string;
  fileUrl?: string;
  onVerify?: (docId: string) => void;
  onReject?: (docId: string) => void;
  onView?: (docId: string) => void;
  className?: string;
}

const DocumentItem = ({
  name,
  doc,
  uploadDate,
  consumerStatus,
  documentId,
  fileUrl,
  onVerify,
  onReject,
  onView,
  className,
}: DocumentItemProps) => {
  const updateDocumentMutation = useUpdateConsumerDocument();
  const [loadingStates, setLoadingStates] = useState<
    Record<string, "verify" | "reject" | null>
  >({});

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Verified":
        return (
          <span className="h-5 w-5 flex items-center justify-center text-green-600 font-bold">
            ✓
          </span>
        );
      case "Rejected":
        return (
          <span className="h-5 w-5 flex items-center justify-center text-red-600 font-bold">
            ✗
          </span>
        );
      case "Pending":
        return (
          <span className="h-5 w-5 flex items-center justify-center text-yellow-600 font-bold">
            ?
          </span>
        );
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "Verified":
        return "Verified";
      case "Rejected":
        return "Rejected";
      case "Pending":
        return "Pending";
      default:
        return status;
    }
  };

  const handleVerify = async (docId: string) => {
    if (!docId) return;

    try {
      // Set loading state for this specific document
      setLoadingStates((prev) => ({ ...prev, [docId]: "verify" }));

      await updateDocumentMutation.mutateAsync({
        document_id: docId,
        payload: {
          action: 1,
        } as any,
      });

      // Call the optional callback
      onVerify?.(docId);
    } catch (error) {
      console.error("Error verifying document:", error);
    } finally {
      // Clear loading state
      setLoadingStates((prev) => ({ ...prev, [docId]: null }));
    }
  };

  const handleReject = async (docId: string) => {
    if (!docId) return;

    try {
      // Set loading state for this specific document
      setLoadingStates((prev) => ({ ...prev, [docId]: "reject" }));

      await updateDocumentMutation.mutateAsync({
        document_id: docId,
        payload: {
          action: 2,
        } as any,
      });

      // Call the optional callback
      onReject?.(docId);
    } catch (error) {
      console.error("Error rejecting document:", error);
    } finally {
      // Clear loading state
      setLoadingStates((prev) => ({ ...prev, [docId]: null }));
    }
  };

  const handleView = (docData: any) => {
    const urlToOpen = docData.file || fileUrl;
    if (urlToOpen) {
      window.open(urlToOpen, "_blank", "noopener,noreferrer");
    } else {
      console.warn("No file URL provided for document");
    }

    onView?.(docData.id);
  };

  // Remove duplicates based on documentType and createdDate
  const uniqueDocs = React.useMemo(() => {
    if (!Array.isArray(doc)) return [];

    return Array.from(
      new Map(
        doc.map((item: any) => [
          `${item.documentType}_${item.createdDate}_${item.id}`,
          item,
        ])
      ).values()
    );
  }, [doc]);

  if (!uniqueDocs.length) {
    return <div className="text-gray-500">No documents available</div>;
  }

  return (
    <div className={cn("space-y-4", className)}>
      {uniqueDocs.map((data: any) => {
        const isVerifying = loadingStates[data.id] === "verify";
        const isRejecting = loadingStates[data.id] === "reject";
        const isLoading = isVerifying || isRejecting;

        return (
          <div
            key={`${data.id}_${data.documentType}_${data.createdDate}`}
            className="flex items-start p-3 border rounded-lg bg-white shadow-sm"
          >
            <div className="mr-3 text-gray-400 flex-shrink-0">
              <FileText className="h-6 w-6" />
            </div>

            <div className="flex-grow min-w-0">
              <div className="font-medium text-gray-900 truncate">
                {data.documentType}
              </div>
              <div className="text-xs text-gray-500">
                Uploaded on {data.createdDate}
              </div>
            </div>

            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Status Display */}
              <div className="flex items-center">
                {getStatusIcon(data.statusDisplay)}
                <span className="text-sm ml-1 font-medium">
                  {getStatusText(data.statusDisplay)}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {data.statusDisplay === "Pending" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-600 hover:bg-green-50"
                      onClick={() => handleVerify(data.id)}
                      disabled={isLoading}
                    >
                      {isVerifying ? "Verifying..." : "Verify"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => handleReject(data.id)}
                      disabled={isLoading}
                    >
                      {isRejecting ? "Rejecting..." : "Reject"}
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleView(data)}
                  disabled={!data.file && !fileUrl}
                  className="hover:bg-gray-50"
                >
                  View
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DocumentItem;
